import React, { useCallback, useMemo, useState } from "react"
import { ClassProps } from "../../utility/common/props"
import { Form } from "react-bootstrap"
import { Select } from "../Select/Select"
import { ValueType } from "react-select"
import AsyncSearchableInput, { OptionType } from "../AsyncSearchableInput/AsyncSearchableInput"
import { formTranslation } from "../../locales/form"
import {
    optionToKnowledgeBaseAccessType,
    projectsToOptions,
    searchUsersAndRolesByProject
} from "../../utility/knowledgeBase/accessOptions"
import {
    KnowledgeBasePermittedAction,
    KnowledgeBasePermittedActionSelectItem
} from "../../models/knowledgeBase/permission"
import KnowledgeBaseUserAccessItems from "../KnowledgeBaseUserAccessItems/KnowledgeBaseUserAccessItems"
import {
    KnowledgeBaseSubscriptionSelectItem,
    KnowledgeBaseSubscriptionType
} from "../../models/knowledgeBase/subscription"
import { isSameUserAccessItem, permissionWeightMapper } from "../../utility/knowledgeBase/knowledgeBase"
import { UserAccessPrimitive } from "../../models/knowledgeBase/userAccess"
import { Project } from "../../models/project"
import { useField } from "formik"
import styles from "./UserAccess.module.scss"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "../../store/hooks"
import { searchUsersAndRolesToLink } from "../../utility/dialogs/clientLinkOptions"
import { MAX_LINK_SIZE } from "../LinkClientToOperatorPopover/LinkClientToOperatorPopoverWrapper"

export type UserAssessItemType = "subscription" | "permission" | "newsline"

export interface UserAccessCommonProps {
    subscriptionOptions?: KnowledgeBaseSubscriptionSelectItem[]
    permittedActionOptions?: KnowledgeBasePermittedActionSelectItem[]
    currentProject?: Project
    projects?: Project[]
    disableProjectsSelector?: boolean
    clientLinks?: boolean
    clientId?: string
}

type Deps = UserAccessCommonProps & ClassProps

export interface UserAccessProps extends Deps {
    type: UserAssessItemType
    name: string
    title: string
    rolesPlaceholder?: string
    onAddToAddedList: (value: UserAccessPrimitive) => void
    onAddToRemovedList: (value: UserAccessPrimitive) => void
}

function UserAccess(props: UserAccessProps) {
    const {
        type,
        name,
        title,
        projects,
        currentProject,
        rolesPlaceholder,
        subscriptionOptions,
        permittedActionOptions,
        onAddToAddedList,
        onAddToRemovedList,
        disableProjectsSelector = false,
        clientLinks = false,
        clientId = ""
    } = props
    const [field, meta, helpers] = useField<UserAccessPrimitive[]>(name)
    const { setValue } = helpers
    const projectOptions = useMemo(() => projectsToOptions(projects || []), [projects])
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [selectedProjectId, setSelectedProjectId] = useState(currentProject?.id)
    const [selectedProjectTitle, setSelectedProjectTitle] = useState(currentProject?.name)

    const getOptions = useCallback(
        clientLinks && clientId
            ? searchUsersAndRolesToLink(dispatch, { ClientId: clientId, Query: "", Size: MAX_LINK_SIZE })
            : searchUsersAndRolesByProject(),
        [clientLinks, clientId]
    )

    const add = (item: UserAccessPrimitive) => {
        const itemList = field.value
        const alreadyAdded = itemList.some(u => isSameUserAccessItem(u, item))
        const updatedItemsList = alreadyAdded ? itemList : [item, ...itemList]
        onAddToAddedList(item)
        setValue(updatedItemsList, true)
    }

    const remove = (item: UserAccessPrimitive) => {
        const itemList = field.value.filter(u => !isSameUserAccessItem(u, item))
        onAddToRemovedList(item)
        setValue(itemList, true)
    }

    const replace = (item: UserAccessPrimitive) => {
        const itemList = field.value.map(u => (isSameUserAccessItem(u, item) ? item : u))
        onAddToAddedList(item)
        setValue(itemList, true)
    }

    const handleUserOrRoleSelect = (option: ValueType<OptionType, false>) => {
        if (selectedProjectId && option) {
            const permissionType = optionToKnowledgeBaseAccessType(option)
            if (permissionType) {
                switch (type) {
                    case "subscription":
                        add({
                            Type: permissionType,
                            SubscriptionType: KnowledgeBaseSubscriptionType.Notify,
                            Value: option.value,
                            ProjectId: selectedProjectId
                        })
                        break
                    case "permission":
                        const action = KnowledgeBasePermittedAction.Edit
                        add({
                            Type: permissionType,
                            Action: action,
                            Value: option.value,
                            ProjectId: selectedProjectId,
                            SearchValue: "",
                            Weight: permissionWeightMapper(permissionType, action)
                        })
                        break
                    case "newsline":
                        add({
                            Type: permissionType,
                            Value: option.value,
                            ProjectId: selectedProjectId
                        })
                        break
                    default:
                        break
                }
            }
        }
    }

    const handleProjectSelect = (option: ValueType<OptionType, false>) => {
        if (option) {
            setSelectedProjectId(option.value)
            setSelectedProjectTitle(option.label)
        }
    }

    return (
        <div className={styles.userAccess}>
            <Form.Label>{title}</Form.Label>
            {!disableProjectsSelector && (
                <Form.Group>
                    <Select
                        isClearable
                        menuPlacement="auto"
                        value={{
                            label: selectedProjectTitle || "",
                            value: selectedProjectId || ""
                        }}
                        isDisabled={!projectOptions.length}
                        options={projectOptions}
                        noOptionsMessage={() => t(formTranslation.noResultsFound)}
                        onChange={handleProjectSelect}
                    />
                </Form.Group>
            )}
            <AsyncSearchableInput
                key={selectedProjectId}
                id="search-users-and-roles-by-project"
                name="search-users-and-roles-by-project"
                optionsMeta={selectedProjectId}
                getOptions={getOptions}
                onChange={handleUserOrRoleSelect}
                placeholder={rolesPlaceholder}
            />
            <KnowledgeBaseUserAccessItems
                data={field.value}
                subscriptionOptions={subscriptionOptions}
                permittedActionOptions={permittedActionOptions}
                projectId={selectedProjectId}
                onChange={replace}
                onRemove={remove}
            />
            {meta.error && <div className={styles.userAccess__error}>{meta.error}</div>}
        </div>
    )
}

export default UserAccess
